import { ReactNode, ElementType } from 'react';

import { parseResponsive, Breakpoint } from './helpers/layout';

export interface TextProps {
  as?: ElementType;
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'body'
    | 'caption'
    | 'xs'
    | 'label'
    | 'tiny';
  font?: 'bangers' | 'outfit' | 'sans' | 'street';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold' | 'black';
  color?:
    | 'default'
    | 'foreground'
    | 'muted'
    | 'primary'
    | 'accent'
    | 'danger'
    | 'success'
    | 'white';
  align?: 'left' | 'center' | 'right';
  size?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | 'huge';
  tracking?: 'tighter' | 'tight' | 'normal' | 'wide' | 'wider' | 'widest';
  leading?: 'none' | 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose';
  uppercase?: boolean;
  span?: boolean;
  maxWidth?:
    | 'xs'
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl';
  display?:
    | 'block'
    | 'inline-block'
    | 'inline'
    | 'flex'
    | 'inline-flex'
    | 'none';
  children: ReactNode;
}

const SIZE_MAP: Record<string, Record<Breakpoint, string>> = {
  xs: {
    default: 'text-xs',
    sm: 'sm:text-xs',
    md: 'md:text-xs',
    lg: 'lg:text-xs',
    xl: 'xl:text-xs',
    '2xl': '2xl:text-xs',
  },
  sm: {
    default: 'text-sm',
    sm: 'sm:text-sm',
    md: 'md:text-sm',
    lg: 'lg:text-sm',
    xl: 'xl:text-sm',
    '2xl': '2xl:text-sm',
  },
  md: {
    default: 'text-base',
    sm: 'sm:text-base',
    md: 'md:text-base',
    lg: 'lg:text-base',
    xl: 'xl:text-base',
    '2xl': '2xl:text-base',
  },
  lg: {
    default: 'text-lg',
    sm: 'sm:text-lg',
    md: 'md:text-lg',
    lg: 'lg:text-lg',
    xl: 'xl:text-lg',
    '2xl': '2xl:text-lg',
  },
  xl: {
    default: 'text-xl',
    sm: 'sm:text-xl',
    md: 'md:text-xl',
    lg: 'lg:text-xl',
    xl: 'xl:text-xl',
    '2xl': '2xl:text-xl',
  },
  '2xl': {
    default: 'text-2xl',
    sm: 'sm:text-2xl',
    md: 'md:text-2xl',
    lg: 'lg:text-2xl',
    xl: 'xl:text-2xl',
    '2xl': '2xl:text-2xl',
  },
  '3xl': {
    default: 'text-3xl',
    sm: 'sm:text-3xl',
    md: 'md:text-3xl',
    lg: 'lg:text-3xl',
    xl: 'xl:text-3xl',
    '2xl': '2xl:text-3xl',
  },
  '4xl': {
    default: 'text-4xl',
    sm: 'sm:text-4xl',
    md: 'md:text-4xl',
    lg: 'lg:text-4xl',
    xl: 'xl:text-4xl',
    '2xl': '2xl:text-4xl',
  },
  '5xl': {
    default: 'text-5xl',
    sm: 'sm:text-5xl',
    md: 'md:text-5xl',
    lg: 'lg:text-5xl',
    xl: 'xl:text-5xl',
    '2xl': '2xl:text-5xl',
  },
  '6xl': {
    default: 'text-6xl',
    sm: 'sm:text-6xl',
    md: 'md:text-6xl',
    lg: 'lg:text-6xl',
    xl: 'xl:text-6xl',
    '2xl': '2xl:text-6xl',
  },
  huge: {
    default: 'text-[10rem]',
    sm: 'sm:text-[10rem]',
    md: 'md:text-[10rem]',
    lg: 'lg:text-[10rem]',
    xl: 'xl:text-[10rem]',
    '2xl': '2xl:text-[10rem]',
  },
};

export const Text = ({
  as,
  variant = 'body',
  font,
  weight,
  color,
  align,
  size,
  tracking,
  leading,
  uppercase,
  span,
  maxWidth,
  display,
  children,
}: TextProps) => {
  const Component = span
    ? 'span'
    : as || (variant.startsWith('h') ? (variant as ElementType) : 'p');

  const VARIANT_SIZE: Partial<
    Record<NonNullable<TextProps['variant']>, string>
  > = {
    h1: 'text-5xl md:text-7xl lg:text-[10rem]',
    h2: 'text-4xl md:text-5xl',
    h3: 'text-2xl md:text-3xl',
    h4: 'text-xl',
    body: 'text-base md:text-lg',
    caption: 'text-sm',
    xs: 'text-xs',
    label: 'text-xs',
    tiny: 'text-[10px]',
  };

  const VARIANT_STYLE: Partial<
    Record<NonNullable<TextProps['variant']>, string>
  > = {
    h1: 'tracking-tighter uppercase leading-none',
    h2: 'font-bold tracking-tight',
    h3: 'font-bold',
    h4: 'font-semibold',
    body: 'leading-relaxed',
    label: 'uppercase tracking-widest',
  };

  const styles = [
    size ? '' : (VARIANT_SIZE[variant] ?? ''),
    VARIANT_STYLE[variant] ?? '',

    parseResponsive(size, SIZE_MAP),
    font === 'bangers' ? 'font-bangers' : '',
    font === 'outfit' ? 'font-outfit' : '',
    font === 'sans' ? 'font-sans' : '',
    font === 'street' ? 'font-street' : '',

    weight === 'normal' ? 'font-normal' : '',
    weight === 'medium' ? 'font-medium' : '',
    weight === 'semibold' ? 'font-semibold' : '',
    weight === 'bold' ? 'font-bold' : '',
    weight === 'black' ? 'font-black' : '',

    color === 'default' ? 'text-foreground' : '',
    color === 'foreground' ? 'text-foreground' : '',
    color === 'muted' ? 'text-slate-600' : '',
    color === 'primary' ? 'text-primary' : '',
    color === 'accent' ? 'text-accent' : '',
    color === 'danger' ? 'text-danger' : '',
    color === 'success' ? 'text-success' : '',
    color === 'white' ? 'text-white' : '',

    align === 'left' ? 'text-left' : '',
    align === 'center' ? 'text-center' : '',
    align === 'right' ? 'text-right' : '',

    tracking ? `tracking-${tracking}` : '',
    leading ? `leading-${leading}` : '',
    uppercase ? 'uppercase' : '',
    maxWidth ? `max-w-${maxWidth}` : '',
    display ? display : '',
  ]
    .filter(Boolean)
    .join(' ');

  return <Component className={styles}>{children}</Component>;
};
